import React, { useState, useEffect, useMemo } from 'react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import { Minus, Plus, ShoppingCart, Truck, Package, MapPin, Zap } from 'lucide-react';
import { toast } from 'sonner';
import StarRating from './StarRating';
import ProductImageZoom from './ProductImageZoom';
import axios from 'axios';

export default function PhysicalProductView({ product, store }) {
    const { user } = useAuth();
    const { addToCart, items, shippingAddress } = useCart();
    const navigate = useNavigate();
    const isSupplierStore = store?.store_type?.toUpperCase() === 'SUPPLIER';
    const minQuantity = (product?.is_resell || !isSupplierStore) ? 1 : (product?.min_wholesale_quantity || 1);
    const [quantity, setQuantity] = useState(minQuantity);
    const [selectedVariant, setSelectedVariant] = useState(null);
    const [selectedOptions, setSelectedOptions] = useState({});
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [estimatedOptions, setEstimatedOptions] = useState(null);
    const [calculatingDelivery, setCalculatingDelivery] = useState(false);

    // 1. Extract Attributes and Values from Variants
    const attributes = useMemo(() => {
        if (!product.variants || product.variants.length === 0) return {};

        const attrs = {};
        product.variants.forEach(variant => {
            if (variant.attributes) {
                Object.entries(variant.attributes).forEach(([key, value]) => {
                    if (!attrs[key]) attrs[key] = new Set();
                    attrs[key].add(value);
                });
            }
        });

        // Convert Sets to Arrays
        const formattedAttrs = {};
        Object.keys(attrs).forEach(key => {
            formattedAttrs[key] = Array.from(attrs[key]);
        });
        return formattedAttrs;
    }, [product.variants]);

    // 2. Initialize Selection
    useEffect(() => {
        if (Object.keys(attributes).length > 0 && Object.keys(selectedOptions).length === 0) {
            const initialOptions = {};
            Object.keys(attributes).forEach(key => {
                initialOptions[key] = attributes[key][0];
            });
            setSelectedOptions(initialOptions);
        }
    }, [attributes]);

    // 3. Find Matching Variant
    useEffect(() => {
        if (!product.variants || product.variants.length === 0) {
            setSelectedVariant(null);
            return;
        }

        const match = product.variants.find(v => {
            if (!v.attributes) return false;
            return Object.entries(selectedOptions).every(([key, val]) => v.attributes[key] === val);
        });

        setSelectedVariant(match || null);
    }, [selectedOptions, product.variants]);

    // Determine display values based on selection or base product
    const displayPrice = selectedVariant ? (selectedVariant.price || product.price) : product.price;
    const baseStock = selectedVariant ? (selectedVariant.stock || 0) : product.stock_quantity;

    // Decrease stock by the amount already in the cart for the exact product & variant combo
    const inCartQty = items?.reduce((acc, cartItem) => {
        if (cartItem.product.id === product.id && JSON.stringify(cartItem.variants || {}) === JSON.stringify(selectedOptions || {})) {
            return acc + cartItem.quantity;
        }
        return acc;
    }, 0) || 0;

    const displayStock = Math.max(0, baseStock - inCartQty);
    const hasDiscount = !!product.discount_price && !selectedVariant; // Only apply base discount if no variant (or handle variant discounts later)
    const discountPercent = hasDiscount
        ? Math.round(((product.price - product.discount_price) / product.price) * 100)
        : 0;

    // Images logic: Variant images > Product images > Placeholder
    const images = useMemo(() => {
        let imgs = product.images;
        if (selectedVariant && selectedVariant.images && selectedVariant.images.length > 0) {
            imgs = selectedVariant.images;
        }
        return (imgs && imgs.length > 0)
            ? imgs
            : ["https://images.pexels.com/photos/5632399/pexels-photo-5632399.jpeg?auto=compress&cs=tinysrgb&w=800"];
    }, [product.images, selectedVariant]);

    // Reset image index when variant changes
    useEffect(() => {
        setCurrentImageIndex(0);
    }, [selectedVariant]);

    // Calculate real-time delivery estimation
    useEffect(() => {
        const fetchDeliveryEstimate = async () => {
            if (!product?.id || !store?.id) return;
            if (!user && !shippingAddress) return;

            const defaultAddr = shippingAddress || (user && user.saved_addresses ? (user.saved_addresses.find(a => a.id === user.default_address_id) || user.saved_addresses[0]) : null);
            if (!defaultAddr) return;

            setCalculatingDelivery(true);
            try {
                const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";
                const loc = {
                    latitude: defaultAddr.coordinates?.lat || 18.5392,
                    longitude: defaultAddr.coordinates?.lon || defaultAddr.coordinates?.lng || -72.3350,
                    address: defaultAddr.address || "Port-au-Prince"
                };

                const deliveryItems = [{
                    product_id: product.id,
                    quantity: quantity,
                    weight_lb: parseFloat(product.delivery_settings?.weight || 0.5)
                }];

                const res = await axios.post(`${API_URL}/delivery/calculate`, {
                    store_id: store.id,
                    items: deliveryItems,
                    location: loc
                });

                if (res.data && res.data.options && res.data.options.length > 0) {
                    setEstimatedOptions(res.data.options);
                } else if (res.data) {
                    setEstimatedOptions([{
                        id: "seller_std",
                        name: `Livraison par ${store.name}`,
                        price: res.data.delivery_fee,
                        type: "SELLER"
                    }]);
                }
            } catch (err) {
                console.error("Error calculating delivery estimate:", err);
            } finally {
                setCalculatingDelivery(false);
            }
        };

        const timeoutId = setTimeout(() => {
            fetchDeliveryEstimate();
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [user, shippingAddress, product?.id, store?.id, quantity, product?.delivery_settings]);

    const isMoreSure = ["moresure_client", "moresure_free"].includes(product.shipping_type || product.delivery_settings?.type);

    // Use estimated_shipping_cost from backend if available, otherwise check store threshold
    const freeThreshold = store?.delivery_criteria?.free_delivery_threshold || 0;
    const shippingCost = product.estimated_shipping_cost !== undefined
        ? product.estimated_shipping_cost
        : (freeThreshold > 0 && displayPrice >= freeThreshold ? 0 : null);

    // Check if shipping type implies payment to avoid showing "Free" when cost is 0 (uncalculated)
    const isPaidType = ['CARRIER_PAID', 'moresure_client'].includes(product.shipping_type || product.delivery_settings?.type);
    const isFreeShipping = shippingCost === 0 && !isPaidType;

    let deliveryDisplay = null;
    if (calculatingDelivery) {
        deliveryDisplay = <span className="text-gray-500 italic">Calcul en cours...</span>;
    } else if (estimatedOptions && estimatedOptions.length > 0) {
        const primaryOption = estimatedOptions[0];
        const timeDisplay = primaryOption.estimated_time ? ` (🕒 ${primaryOption.estimated_time})` : '';

        if (primaryOption.price === 0) {
            deliveryDisplay = <span className="font-semibold text-green-600">Livraison GRATUITE {timeDisplay} <span className="text-xs text-gray-500 font-normal">({primaryOption.name})</span></span>;
        } else {
            deliveryDisplay = <span className="font-semibold">Livraison estimée: {primaryOption.price.toFixed(2)} HTG {timeDisplay} <span className="text-xs text-gray-500 font-normal">({primaryOption.name})</span></span>;
        }
    } else {
        // Fallback UI
        deliveryDisplay = <span className="font-semibold">
            {isFreeShipping ? 'Livraison GRATUITE' : (shippingCost > 0 ? `Livraison: ${shippingCost.toFixed(2)} HTG` : 'Calculé au panier')}
        </span>;
    }

    const handleAddToCart = async () => {
        if (product.has_variants && !selectedVariant) {
            toast.error("Veuillez sélectionner les options du produit");
            return;
        }

        const success = await addToCart(product, quantity, null, selectedOptions);
        if (success) {
            toast.success(`${product.title} ajouté au panier !`);
        }
    };

    const handleBuyNow = async () => {
        if (displayStock === 0 && inCartQty > 0) {
            navigate('/checkout');
            return;
        }

        if (product.has_variants && !selectedVariant) {
            toast.error("Veuillez sélectionner les options du produit");
            return;
        }
        const success = await addToCart(product, quantity, null, selectedOptions);
        if (success) {
            navigate('/checkout');
        }
    };

    const handleOptionSelect = (attribute, value) => {
        setSelectedOptions(prev => ({
            ...prev,
            [attribute]: value
        }));
    };

    return (
        <div className="grid md:grid-cols-2 gap-8">
            {/* LEFT: Image Gallery */}
            <div className="space-y-4">
                <div className="relative aspect-square bg-gray-50 rounded-xl overflow-hidden border border-gray-200">
                    {hasDiscount && (
                        <Badge className="absolute top-4 left-4 bg-red-600 text-white z-10">
                            -{discountPercent}%
                        </Badge>
                    )}
                    <ProductImageZoom
                        src={images[currentImageIndex]}
                        alt={product.title}
                    />
                </div>

                {/* Thumbnails */}
                {images.length > 1 && (
                    <div className="flex gap-2 overflow-x-auto pb-2">
                        {images.map((img, idx) => (
                            <button
                                key={idx}
                                onClick={() => setCurrentImageIndex(idx)}
                                className={`flex-shrink-0 w-20 h-20 rounded-lg border-2 overflow-hidden ${currentImageIndex === idx ? 'border-orange-500' : 'border-gray-200'
                                    }`}
                            >
                                <img src={img} alt="" className="w-full h-full object-cover" />
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* RIGHT: Product Info */}
            <div className="space-y-6">
                <div>
                    {store && (
                        <Link to={`/boutique/${store.slug}`} className="text-orange-600 font-semibold hover:underline mb-1 inline-block">
                            {store.name}
                        </Link>
                    )}
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">{product.title}</h1>
                    <div className="flex items-center gap-2">
                        <StarRating
                            rating={product.average_rating || 0}
                            totalReviews={product.total_reviews || 0}
                            size="lg"
                            showCount={true}
                        />
                    </div>
                </div>

                <div className="flex items-baseline gap-3">
                    <span className="text-4xl font-bold text-orange-600">{Number(displayPrice).toFixed(2)} HTG</span>
                    {hasDiscount && (
                        <span className="text-xl text-gray-500 line-through">{product.price.toFixed(2)} HTG</span>
                    )}
                </div>

                {/* Variant Selectors */}
                {Object.keys(attributes).length > 0 && (
                    <div className="space-y-4">
                        {Object.entries(attributes).map(([attrName, values]) => (
                            <div key={attrName}>
                                <div className="text-sm font-medium text-gray-900 mb-2">
                                    {attrName}: <span className="text-gray-500">{selectedOptions[attrName]}</span>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {values.map(value => (
                                        <button
                                            key={value}
                                            onClick={() => handleOptionSelect(attrName, value)}
                                            className={`px-4 py-2 rounded-md text-sm font-medium border transition-all ${selectedOptions[attrName] === value
                                                ? 'border-orange-500 bg-orange-50 text-orange-700 ring-1 ring-orange-500'
                                                : 'border-gray-200 text-gray-700 hover:border-gray-300'
                                                }`}
                                        >
                                            {value}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Stock Status */}
                {displayStock <= 5 && displayStock > 0 && (
                    <div className="text-orange-600 font-medium text-sm bg-orange-50 p-2 rounded inline-block">
                        ⚠️ Plus que {displayStock} en stock !
                    </div>
                )}
                {displayStock === 0 && (
                    <div className="text-red-600 font-bold bg-red-50 p-2 rounded inline-block">
                        ❌ Rupture de stock
                    </div>
                )}



                {/* Shipping Info */}
                <Card className="p-4 bg-gray-50 border-none">
                    <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                            <Truck className="h-5 w-5 text-gray-600" />
                            {deliveryDisplay}
                            {product.shipping_type && product.shipping_type !== 'STORE_STANDARD' && (
                                <Badge variant="outline" className="text-[10px] h-5 px-1 ml-2 bg-white">
                                    {product.shipping_type === 'STORE_FREE_ZONE' ? 'Zone Gratuite' :
                                        product.shipping_type === 'CARRIER_PAID' ? 'Transporteur' :
                                            product.shipping_type === 'CARRIER_MARKETING' ? 'Transporteur (Offert)' :
                                                product.shipping_type}
                                </Badge>
                            )}
                        </div>

                        {product.allowed_delivery_types?.includes('PICKUP') && (
                            <div className="flex items-center gap-2 text-amber-700 font-medium italic">
                                <Package className="h-4 w-4" />
                                <span>Retrait en boutique (Pickup) disponible</span>
                            </div>
                        )}

                        {product.allowed_delivery_types?.includes('EXPRESS') && store?.delivery_criteria?.allow_express && (parseFloat(store?.delivery_criteria?.price_per_lb ?? 0) > 0 && "price_per_km" in (store?.delivery_criteria || {})) && (
                            <div className="flex items-center gap-2 text-orange-600 font-bold bg-orange-50 p-2 rounded-lg mt-2 border border-orange-100 animate-in fade-in">
                                <Zap className="h-5 w-5 fill-current" />
                                <span>Livraison Express disponible ({store.delivery_criteria?.express_delivery_time || "1 à 24H"})</span>
                            </div>
                        )}

                        <div className="flex items-center gap-2 text-gray-600">
                            <Package className="h-4 w-4" />
                            <span>{isMoreSure ? 'Expédié par MoreSure' : `Expédié par ${store?.name || 'Vendeur'}`}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                            <MapPin className="h-4 w-4 flex-shrink-0" />
                            <div className="flex flex-col">
                                {user || shippingAddress ? (
                                    (() => {
                                        const defaultAddr = shippingAddress || (user?.saved_addresses?.find(a => a.id === user.default_address_id) || user?.saved_addresses?.[0]);
                                        return defaultAddr ? (
                                            <span>Livraison à <strong>{defaultAddr.city || defaultAddr.address}</strong></span>
                                        ) : (
                                            <span className="flex flex-wrap items-center gap-1">
                                                Pas d'adresse par défaut.
                                                <Link to="/profile" className="text-orange-600 font-semibold hover:underline">
                                                    Ajouter une adresse
                                                </Link>
                                            </span>
                                        );
                                    })()
                                ) : (
                                    <span>
                                        <Link to="/auth" className="text-orange-600 hover:underline">Connectez-vous</Link> pour voir les options
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </Card>

                {/* Item Details Table (New Requirement) */}
                <div className="mt-4 border rounded-lg bg-white overflow-hidden shadow-sm">
                    <div className="bg-gray-50 px-4 py-2.5 border-b">
                        <h3 className="font-bold text-gray-900 flex items-center gap-2 text-sm uppercase tracking-wide">
                            <Package className="h-4 w-4 text-orange-600" />
                            Détails de l'article
                        </h3>
                    </div>
                    <div className="divide-y text-sm">
                        <div className="grid grid-cols-2 px-4 py-2 hover:bg-gray-50 transition-colors">
                            <span className="text-gray-500">Poids</span>
                            <span className="text-gray-900 font-semibold">{product.delivery_settings?.weight || product.specifications?.weight_lb || '0.5'} Lbs</span>
                        </div>
                        {product.condition && (
                            <div className="grid grid-cols-2 px-4 py-2 hover:bg-gray-50 transition-colors">
                                <span className="text-gray-500">État</span>
                                <span className="text-gray-900 font-semibold">{product.condition === 'NEW' ? 'Neuf' : 'Occasion'}</span>
                            </div>
                        )}
                        {product.specifications && Object.entries(product.specifications).map(([key, value]) => (
                            !['weight_lb', 'weight', 'condition'].includes(key.toLowerCase()) && (
                                <div key={key} className="grid grid-cols-2 px-4 py-2 hover:bg-gray-50 transition-colors">
                                    <span className="text-gray-500 capitalize">{key.replace(/_/g, ' ')}</span>
                                    <span className="text-gray-900 font-semibold">{String(value)}</span>
                                </div>
                            )
                        ))}
                        {store && (
                            <div className="grid grid-cols-2 px-4 py-2 hover:bg-gray-50 transition-colors">
                                <span className="text-gray-500">Vendeur</span>
                                <span className="text-gray-900 font-semibold">{store.name}</span>
                            </div>
                        )}
                        {(product.return_policy_days > 0) ? (
                            <div className="grid grid-cols-2 px-4 py-2 hover:bg-gray-50 transition-colors">
                                <span className="text-gray-500">Politique de retour</span>
                                <span className="text-green-600 font-semibold">Accepté sous {product.return_policy_days} jours</span>
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 px-4 py-2 hover:bg-gray-50 transition-colors">
                                <span className="text-gray-500">Politique de retour</span>
                                <span className="text-gray-900 font-semibold">Non acceptés</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Actions */}
                <div className="flex flex-col gap-4">
                    <div className="flex items-center gap-4">
                        <span className="font-semibold text-gray-700">Quantité:</span>
                        <div className="flex items-center border border-gray-300 rounded-lg bg-white">
                            <button
                                onClick={() => setQuantity(Math.max(minQuantity, quantity - 1))}
                                className="p-2 hover:bg-gray-100 text-gray-600"
                                disabled={quantity <= minQuantity}
                            >
                                <Minus className="h-4 w-4" />
                            </button>
                            <span className="px-6 font-medium w-12 text-center">{quantity}</span>
                            <button
                                onClick={() => setQuantity(Math.min(displayStock, quantity + 1))}
                                className="p-2 hover:bg-gray-100 text-gray-600"
                                disabled={quantity >= displayStock}
                            >
                                <Plus className="h-4 w-4" />
                            </button>
                        </div>
                    </div>

                    {(minQuantity > 1) && (
                        <div className="text-sm text-gray-500 italic text-center md:text-left mt-[-8px]">
                            Minimum de commande : {minQuantity} unité(s)
                        </div>
                    )}

                    <div className="flex gap-3 mt-4">
                        <Button
                            onClick={handleAddToCart}
                            disabled={displayStock === 0}
                            variant="outline"
                            className="flex-1 gap-2 h-12 text-lg border-2"
                        >
                            <ShoppingCart className="h-5 w-5" />
                            {displayStock === 0 && inCartQty > 0 ? "Limite au panier" : "Ajouter au panier"}
                        </Button>
                        <Button
                            onClick={handleBuyNow}
                            disabled={displayStock === 0 && inCartQty === 0}
                            className="flex-1 bg-orange-600 hover:bg-orange-700 h-12 text-lg shadow-lg hover:shadow-xl transition-all"
                        >
                            {displayStock === 0 && inCartQty > 0 ? "Payer le panier" : "Acheter maintenant"}
                        </Button>
                    </div>

                    {displayStock === 0 && inCartQty > 0 && (
                        <div className="text-center text-sm text-red-500 font-semibold mt-2">
                            Vous avez déjà ajouté la quantité maximale disponible ({inCartQty}) dans votre panier.
                        </div>
                    )}
                </div>
            </div>
        </div >
    );
}
